/// <amd-module name="Wiki/PageTree/Utilities/WikiPageFilterProvider" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { WikiPage } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { IWikiFilterService } from "Wiki/Filter/IWikiFilterService";
import { FilteredWikiPage } from "Wiki/PageTree/Contracts";
import { getParentPaths } from "Wiki/PageTree/Helper";

export interface WikiPageSearchResults {
    results: WikiPage[];
    exactMatchedPagesCount: number;
}

export class WikiPageFilterProvider {
    constructor(
        private _pageContext: IVssPageContext,
        private _source: string,
        private _getPagesForFiltering: () => Promise<WikiPage[]>,
        private _errorHandler: (e: Error) => void
    ) {}

    public filter(filterText: string): Promise<WikiPageSearchResults | void> {
        return this._prepareForFiltering().then((values: [IWikiFilterService | undefined, WikiPage[]]) => {
            let result: FilteredWikiPage[] = [];
            const filterService = values[0];
            const allPages = values[1];
            let exactMatches = 0;

            if (filterService) {
                // TODO: TASK 1459477: optimize to not set it again and again - save O(n)
                filterService.setAllPages(allPages);
                result = filterService.filter(filterText);
                exactMatches = result.length;

                // In tree we want to show filtered items + all parent items so let's start another O(n)
                const additionalPaths: { [key: string]: boolean } = {};
                const existingPaths: { [key: string]: boolean } = {}; // to check if page already exists in results

                for (const filteredPage of result) {
                    const parentPaths = getParentPaths(filteredPage.path);

                    for (const parent of parentPaths) {
                        additionalPaths[parent] = true; // if somehow alItemsMap[parent] does not exist -  we deserve the exception
                    }

                    existingPaths[filteredPage.path] = true;
                }

                for (const page of allPages) {
                    if (additionalPaths[page.path] && !existingPaths[page.path]) {
                        result.push({ ...page, pageName: "" });
                    }
                }
            }
            return {
                results: result,
                exactMatchedPagesCount: exactMatches
            } as WikiPageSearchResults;
        }, this._errorHandler);
    }

    private _prepareForFiltering(): Promise<[IWikiFilterService | undefined, WikiPage[]] | void> {
        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        const filterServicePromise = contributionService.getService<IWikiFilterService>("ms.vss-wiki-web.wiki-filter-service");
        const pagesPromise = this._getPagesForFiltering();

        return Promise.all([filterServicePromise as any, pagesPromise]);
    }
}
